// components/movements/MovementsTable.tsx
import React, { useState } from "react";
import { ArtworkMovementDto } from "../../models/Movement";
import { RecordReturnModal } from "./RecordReturnModal";
import type { ArtworkStatusRefData } from "../../models/ArtworkStatus";
import "./MovementsTableStyleSheet.css";
interface Props {
    movements: ArtworkMovementDto[];
    statuses: ArtworkStatusRefData[];
    onRefresh: () => void;
}

const STATUS_BADGE: Record<string, string> = {
    LOAN_OUT: "badge--purple",
    EXHIB: "badge--blue",
    TRANS: "badge--yellow",
    REST: "badge--orange",
    STOR: "badge--grey",
    LOAN_IN: "badge--teal",
};

const getRowClass = (m: ArtworkMovementDto): string => {
    if (m.isOverdue) return "row--overdue";
    if (m.daysUntilDue !== undefined && m.daysUntilDue <= 7) return "row--due-critical";
    if (m.daysUntilDue !== undefined && m.daysUntilDue <= 30) return "row--due-soon";
    return "";
};

const STATUS_CONFIG: Record<string, { label: string; icon: string; cls: string }> = {
    LOAN_OUT: { label: "On Loan", icon: "↗", cls: "badge--blue" },
    LOAN_IN: { label: "Loan In", icon: "↙", cls: "badge--teal" },
    EXHIB: { label: "Exhibition", icon: "🖼", cls: "badge--purple" },
    TRANS: { label: "In Transit", icon: "🚚", cls: "badge--yellow" },
    REST: { label: "Restoration", icon: "🔧", cls: "badge--orange" },
    STOR: { label: "Storage", icon: "📦", cls: "badge--grey" },
    OCONS: { label: "Consignment", icon: "📋", cls: "badge--indigo" },
    AUCT: { label: "Auction", icon: "🔨", cls: "badge--amber" },
};

const StatusBadge: React.FC<{ code: string }> = ({ code }) => {
    const config = STATUS_CONFIG[code] ?? { label: code, icon: "•", cls: "badge--grey" };
    return (
        <span className={`badge badge--icon ${config.cls}`}>
            <span className="badge-icon">{config.icon}</span>
            {config.label}
        </span>
    );
};

const ReturnDateCell: React.FC<{ m: ArtworkMovementDto }> = ({ m }) => {
    if (!m.expectedReturnDate) return <span className="cell-empty">—</span>;

    const formatted = new Date(m.expectedReturnDate).toLocaleDateString("en-GB", {
        day: "numeric", month: "short", year: "numeric"
    });

    if (m.isOverdue) return (
        <div className="return-cell">
            <span className="return-date return-date--overdue">{formatted}</span>
            <span className="return-pill return-pill--overdue">
                ⚠ {m.daysOverdue}d overdue
            </span>
        </div>
    );

    if (m.daysUntilDue !== undefined && m.daysUntilDue <= 7) return (
        <div className="return-cell">
            <span className="return-date return-date--critical">{formatted}</span>
            <span className="return-pill return-pill--critical">
                {m.daysUntilDue}d remaining
            </span>
        </div>
    );

    if (m.daysUntilDue !== undefined && m.daysUntilDue <= 30) return (
        <div className="return-cell">
            <span className="return-date">{formatted}</span>
            <span className="return-pill return-pill--soon">
                {m.daysUntilDue}d remaining
            </span>
        </div>
    );

    return (
        <div className="return-cell">
            <span className="return-date">{formatted}</span>
        </div>
    );
};

const ArtworkCell: React.FC<{ m: ArtworkMovementDto }> = ({ m }) => (
    <div className="artwork-cell">
        {m.thumbnailUrl ? (
            <img src={m.thumbnailUrl} alt={m.artworkTitle}
                className="artwork-cell-thumb" />
        ) : (
            <div className="artwork-cell-thumb artwork-cell-thumb--empty">
                <span>?</span>
            </div>
        )}
        <div className="artwork-cell-meta">
            <div className="cell-title">{m.artworkTitle}</div>
            {m.artistName && (
                <div className="cell-sub">{m.artistName}</div>
            )}
            {m.referenceNumber && (
                <div className="cell-ref">{m.referenceNumber}</div>
            )}
        </div>
    </div>
);

const MovementsTable: React.FC<Props> = ({ movements, statuses, onRefresh }) => {
    const [typeFilter, setTypeFilter] = useState("all");
    const [returnTarget, setReturnTarget] = useState<ArtworkMovementDto | null>(null);
    const [sortField, setSortField] = useState<"expectedReturnDate" | "statusDate">("expectedReturnDate");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
    const [search, setSearch] = useState("");

    const filtered = movements
        .filter(m => typeFilter === "all" || m.statusCode === typeFilter)
        .filter(m => !search ||
            m.artworkTitle.toLowerCase().includes(search.toLowerCase()) ||
            m.artistName?.toLowerCase().includes(search.toLowerCase()) ||
            m.destination?.toLowerCase().includes(search.toLowerCase()) ||
            m.referenceNumber?.toLowerCase().includes(search.toLowerCase()))
        .sort((a, b) => {
            const aVal = a[sortField] ?? "";
            const bVal = b[sortField] ?? "";
            return sortDir === "asc"
                ? aVal < bVal ? -1 : 1
                : aVal > bVal ? -1 : 1;
        });

    const SortHeader: React.FC<{
        field: "expectedReturnDate" | "statusDate";
        label: string;
    }> = ({ field, label }) => (
        <th className="sortable-header"
            onClick={() => {
                if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
                else { setSortField(field); setSortDir("asc"); }
            }}>
            {label}
            <span className="sort-indicator">
                {sortField === field ? (sortDir === "asc" ? " ↑" : " ↓") : " ↕"}
            </span>
        </th>
    );

    return (
        <>
            {/* Filter + search bar */}
            <div className="mt-filter-bar">
                <div className="mt-search">
                    <span className="mt-search-icon">🔍</span>
                    <input
                        className="mt-search-input"
                        placeholder="Search artwork, artist, destination…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    {search && (
                        <button className="mt-search-clear"
                            onClick={() => setSearch("")}>×</button>
                    )}
                </div>

                <div className="mt-type-filters">
                    <button
                        className={`mt-filter-btn ${typeFilter === "all" ? "active" : ""}`}
                        onClick={() => setTypeFilter("all")}
                    >
                        All
                        <span className="mt-filter-count">{movements.length}</span>
                    </button>
                    {Object.entries(STATUS_CONFIG)
                        .filter(([code]) => movements.some(m => m.statusCode === code))
                        .map(([code, config]) => (
                            <button key={code}
                                className={`mt-filter-btn ${typeFilter === code ? "active" : ""}`}
                                onClick={() => setTypeFilter(code)}
                            >
                                {config.icon} {config.label}
                                <span className="mt-filter-count">
                                    {movements.filter(m => m.statusCode === code).length}
                                </span>
                            </button>
                        ))}
                </div>
            </div>

            {/* Results summary */}
            <div className="mt-results-bar">
                <span className="mt-results-count">
                    {filtered.length === movements.length
                        ? `${movements.length} active movements`
                        : `${filtered.length} of ${movements.length} movements`}
                </span>
                {(typeFilter !== "all" || search) && (
                    <button className="mt-clear-filters"
                        onClick={() => { setTypeFilter("all"); setSearch(""); }}>
                        Clear filters
                    </button>
                )}
            </div>

            {/* Table */}
            <div className="mt-table-wrapper">
                <table className="data-table mt-table">
                    <thead>
                        <tr>
                            <th>Artwork</th>
                            <th>Status</th>
                            <th>Destination</th>
                            <SortHeader field="statusDate" label="Departed" />
                            <SortHeader field="expectedReturnDate" label="Expected Return" />
                            <th className="text-right">Insured Value</th>
                            <th className="mt-actions-col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(m => (
                            <tr key={m.historyId} className={getRowClass(m)}>
                                <td><ArtworkCell m={m} /></td>
                                <td><StatusBadge code={m.statusCode} /></td>
                                <td>
                                    <div className="cell-title">
                                        {m.destination ?? "—"}
                                    </div>
                                    {m.contact && (
                                        <div className="cell-sub">{m.contact}</div>
                                    )}
                                </td>
                                <td className="cell-date">
                                    {m.statusDate
                                        ? new Date(m.statusDate).toLocaleDateString("en-GB", {
                                            day: "numeric", month: "short", year: "numeric"
                                        })
                                        : "—"}
                                </td>
                                <td><ReturnDateCell m={m} /></td>
                                <td className="text-right">
                                    {m.insuredValue
                                        ? `${m.insuranceCurrency} ${m.insuredValue.toLocaleString()}`
                                        : <span className="cell-empty">—</span>}
                                </td>
                                <td className="mt-actions-cell">
                                    <button
                                        className="btn btn--ghost btn--small mt-return-btn"
                                        onClick={() => setReturnTarget(m)}
                                        title="Record return"
                                    >
                                        Record Return
                                    </button>
                                </td>
                            </tr>
                        ))}

                        {filtered.length === 0 && (
                            <tr>
                                <td colSpan={7} className="mt-empty">
                                    <div className="mt-empty-inner">
                                        <span className="mt-empty-icon">📭</span>
                                        <p>No movements match your filters.</p>
                                        {(typeFilter !== "all" || search) && (
                                            <button className="btn btn--ghost btn--small"
                                                onClick={() => {
                                                    setTypeFilter("all");
                                                    setSearch("");
                                                }}>
                                                Clear filters
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {returnTarget && (
                <RecordReturnModal
                    movement={returnTarget}
                    statuses={statuses}
                    onClose={() => setReturnTarget(null)}
                    onReturned={() => { setReturnTarget(null); onRefresh(); }}
                />
            )}
        </>
    );
};
export default MovementsTable;